import { z } from 'zod';
import { viaSchema } from '../enums';
import { idSchema } from './common';

/**
 * Historia zmian rekordu (zakładka „Historia”, karta zadania, zasób): usługa `audit.listForObject`
 * (packages/core/src/modules/audit). Czyta `audit_log` tylko wtedy, gdy osoba widzi rekord (loadOrThrow,
 * moduł rekordu); wartości są gotowe do wyświetlenia (etykiety PL, nazwy powiązanych rekordów, daty),
 * bez sekretów, bez kwot bez flagi „Widzi stawki i kwoty” i bez treści długich pól (tylko „zmieniono”).
 */

export const HISTORY_OBJECT_TYPES = ['client', 'contact', 'project', 'task', 'hosted_asset', 'service'] as const;
export type HistoryObjectType = (typeof HISTORY_OBJECT_TYPES)[number];
export const historyObjectTypeSchema = z.enum(HISTORY_OBJECT_TYPES);

export const listHistoryInput = z.object({
  objectType: historyObjectTypeSchema,
  objectId: idSchema,
  /** dołącz rekordy podrzędne (kontakty klienta; elementy, koszty dostawców i umowa zasobu; płatności umowy) */
  includeRelated: z.boolean().default(true),
  limit: z.number().int().min(1).max(200).default(50),
});
export type ListHistoryInput = z.input<typeof listHistoryInput>;

export interface HistoryChange {
  field: string;
  /** etykieta pola po polsku („Status”, „Opiekun”) */
  label: string;
  /** wartości gotowe do wyświetlenia; null = brak */
  from: string | null;
  to: string | null;
  /** wartość nie jest pokazywana (długi tekst, kwota bez uprawnienia) — UI pisze „zmieniono” */
  hidden?: boolean;
}

export interface HistoryActor {
  userId: string;
  name: string;
  initials: string;
  avatarColor: string | null;
}

export interface HistoryEntry {
  id: string;
  at: Date;
  /** akcja z audit_log, np. 'client.update', 'task.move' */
  action: string;
  /** zdanie po polsku: „zmieniono status”, „dodano kontakt” */
  summary: string;
  objectType: string;
  objectId: string | null;
  /** rekord podrzędny, którego dotyczy wpis (np. „Kontakt: Anna Kowalczyk”); null = sam rekord */
  subject: string | null;
  actor: HistoryActor | null;
  actorType: string;
  via: string;
  changes: HistoryChange[];
  /** krótki szczegół (np. treść pozycji checklisty, nazwa dodanego rekordu) */
  detail: string | null;
}

export interface HistoryList {
  items: HistoryEntry[];
  /** true = starsze wpisy pominięte (limit) */
  truncated: boolean;
}

// ── Audit Log w UI (Fala 3: Ustawienia → Audyt; MVP §10, PERMISSIONS §3.5) ──────────────────────────────────────

/**
 * Pełny dziennik workspace'u (nie tylko jednego rekordu): kto, kiedy, co, przez który kanał (UI, AI, MCP, worker).
 * Usługi `audit.list`, `audit.get`, `audit.filterOptions`, `audit.exportCsv` (packages/core/src/modules/audit).
 *
 * Dostęp: Właściciel i Admin — cały dziennik; Manager — podgląd bez wpisów sejfu, zespołu i logowania
 * (`AUDIT_MANAGER_HIDDEN_GROUPS`; zgodnie z rolą: nie zarządza osobami, sejf tylko z osobną flagą); Użytkownik
 * — FORBIDDEN. AI i MCP — FORBIDDEN (`denyVia`). Eksport CSV wymaga „Eksportuje dane”.
 * Wartości jak w historii rekordu (etykiety PL, sekrety tylko jako „ukryte”, kwoty bez „Widzi stawki i kwoty”
 * ukryte, długie pola tylko „zmieniono”). Indeksy `audit_log`: (ws, created_at) — lista i kursor,
 * (ws, actor_user_id, created_at) — osoba, (ws, action, created_at) — akcja, (ws, object_type, object_id) — obiekt
 * i klient (rekordy klienta wybierane po typie obiektu).
 */
export const AUDIT_ACTOR_TYPES = ['user', 'ai', 'mcp', 'worker', 'system'] as const;
export type AuditActorType = (typeof AUDIT_ACTOR_TYPES)[number];

/** Grupy filtra „Rodzaj” (prefiksy akcji albo pełne nazwy). */
export const AUDIT_ACTION_GROUPS = {
  vault: ['vault.', 'credential.'],
  approvals: ['approval.'],
  team: ['team.', 'member.', 'invitation.', 'access_grant.'],
  auth: ['auth.'],
  billing: ['billing.'],
  exports: ['time.export', 'billing.draft.export', 'billing.export', 'export.', 'audit.export', 'file.download'],
} as const;
export type AuditActionGroup = keyof typeof AUDIT_ACTION_GROUPS;
export const AUDIT_ACTION_GROUP_KEYS = Object.keys(AUDIT_ACTION_GROUPS) as AuditActionGroup[];

/** Grupy, których Manager nie widzi (sejf, zarządzanie zespołem, logowania innych osób). */
export const AUDIT_MANAGER_HIDDEN_GROUPS = ['vault', 'team', 'auth'] as const satisfies readonly AuditActionGroup[];

/** Czy akcja należy do grupy (prefiks zakończony kropką albo dokładna nazwa / jej podakcje). */
export function auditActionInGroup(action: string, group: AuditActionGroup): boolean {
  return AUDIT_ACTION_GROUPS[group].some((p) => (p.endsWith('.') ? action.startsWith(p) : action === p));
}

/** Pierwsza grupa akcji (filtr „Rodzaj”, podpis wpisu); null = bez grupy. */
export function auditActionGroupOf(action: string): AuditActionGroup | null {
  return AUDIT_ACTION_GROUP_KEYS.find((g) => auditActionInGroup(action, g)) ?? null;
}

/** Najwięcej wierszy w jednym eksporcie CSV (najnowsze; `truncated`, gdy pasujących jest więcej). */
export const AUDIT_EXPORT_MAX_ROWS = 5000;
export const AUDIT_PAGE_SIZE = 50;

const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Podaj datę w formacie RRRR-MM-DD' });

/** Filtry dziennika (lista, eksport). */
export const auditLogFilterShape = {
  actorUserId: idSchema.optional(),
  /** rodzaj aktora: osoba (UI), AI, MCP, automat (worker), system */
  actorType: z.enum(AUDIT_ACTOR_TYPES).optional(),
  /** kanał wejścia (`audit_log.via`) */
  via: viaSchema.optional(),
  group: z.enum(AUDIT_ACTION_GROUP_KEYS as [AuditActionGroup, ...AuditActionGroup[]]).optional(),
  /** dokładna akcja albo prefiks zakończony kropką ('vault.') */
  action: z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[a-z_][a-zA-Z0-9_.]*$/, { message: 'Nieprawidłowa nazwa akcji' })
    .optional(),
  objectType: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[a-z_]+$/, { message: 'Nieprawidłowy typ obiektu' })
    .optional(),
  objectId: idSchema.optional(),
  /** wpisy rekordów klienta (klient, kontakty, projekty, zadania, czas, rozliczenia, utrzymanie, sejf…) */
  clientId: idSchema.optional(),
  /** zakres dat (Europe/Warsaw, włącznie) */
  from: localDate.optional(),
  to: localDate.optional(),
};

function rangeRefinement(v: { from?: string; to?: string }, ctx: z.RefinementCtx): void {
  if (v.from && v.to && v.from > v.to) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'Data „do” jest przed datą „od”' });
  }
}

export const listAuditLogInput = z
  .object({
    ...auditLogFilterShape,
    limit: z.number().int().min(1).max(200).default(AUDIT_PAGE_SIZE),
    /** kursor z `AuditLogPage.nextCursor` (czas wpisu z dokładnością do mikrosekund + id) */
    cursor: z.string().max(100).nullish(),
  })
  .superRefine(rangeRefinement)
  .default({});
export type ListAuditLogInput = z.input<typeof listAuditLogInput>;
export type AuditLogFilters = Omit<z.output<typeof listAuditLogInput>, 'limit' | 'cursor'>;

export const auditLogRefInput = z.object({ id: idSchema });
export type AuditLogRefInput = z.infer<typeof auditLogRefInput>;

export const exportAuditLogInput = z.object(auditLogFilterShape).superRefine(rangeRefinement).default({});
export type ExportAuditLogInput = z.input<typeof exportAuditLogInput>;

export const auditFilterOptionsInput = z.object({}).strict().optional();

/** Powód ukrycia wartości: sekret (zredagowany przy zapisie), długi tekst, kwota bez uprawnienia, złożona wartość. */
export const AUDIT_HIDDEN_REASONS = ['secret', 'long_text', 'money', 'complex'] as const;
export type AuditHiddenReason = (typeof AUDIT_HIDDEN_REASONS)[number];

export interface AuditChange extends HistoryChange {
  /** dlaczego wartość jest ukryta (gdy `hidden`) */
  hiddenReason?: AuditHiddenReason;
}

export interface AuditClientRef {
  id: string;
  name: string;
  color: string | null;
}

export interface AuditLogItem extends HistoryEntry {
  changes: AuditChange[];
  /** etykieta akcji („Podgląd hasła z sejfu”, „Zmieniono uprawnienia osoby”) */
  actionLabel: string;
  /** grupa filtra „Rodzaj” (sejf, akceptacje, zespół…) */
  group: AuditActionGroup | null;
  /** nazwa rekordu do wyświetlenia („Klient: REA Nieruchomości”, „Dostęp: FTP · helpmycar.pl”) */
  objectLabel: string | null;
  /** ścieżka SPA do rekordu; null = brak widoku albo rekord usunięty */
  objectPath: string | null;
  /** klient rekordu (gdy rekord ma klienta) */
  client: AuditClientRef | null;
  /** urządzenie (z user agent albo meta wpisu, bez adresu IP w liście) */
  device: string | null;
}

export interface AuditLogPage {
  items: AuditLogItem[];
  nextCursor: string | null;
}

export interface AuditLogDetail extends AuditLogItem {
  /** adres IP żądania (tylko szczegóły wpisu) */
  ip: string | null;
  requestId: string;
  /** bezpieczne szczegóły z meta (usługa, kanał konsoli, cel podglądu, powód…) — bez adresów i sekretów */
  context: { label: string; value: string }[];
}

export interface AuditFilterOptions {
  people: { userId: string; name: string; initials: string; avatarColor: string | null; active: boolean }[];
  /** akcje obecne w dzienniku (widoczne dla osoby) */
  actions: { action: string; label: string; group: AuditActionGroup | null }[];
  objectTypes: { type: string; label: string }[];
  clients: AuditClientRef[];
  /** „Eksportuje dane” */
  canExport: boolean;
  /** Manager: podgląd bez wpisów sejfu, zespołu i logowania */
  limited: boolean;
}

export interface AuditLogExport {
  filename: string;
  /** CSV dla Excela PL: UTF-8 z BOM, „;”, CRLF */
  csv: string;
  rows: number;
  /** pasujących wpisów było więcej niż `AUDIT_EXPORT_MAX_ROWS` (eksport ma najnowsze) */
  truncated: boolean;
}

export const AUDIT_MESSAGES = {
  forbidden: 'Dziennik zdarzeń widzą administratorzy i managerowie.',
  invalidCursor: 'Nieprawidłowy kursor listy. Odśwież stronę.',
  notFound: 'Nie znaleziono wpisu dziennika.',
} as const;
